import { exceedsUploadLimit, fileTooLargeMessage } from '../../utils/uploadLimits';
import { isAllowedSupportAttachment, normalizeDocumentFile } from '../../utils/documentFile';
import i18n from '../../i18n';

export const SUPPORT_CATEGORIES = ['problem', 'feedback', 'question'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];
export type SupportStatus = 'open' | 'closed';

/** Muss zu UploadLimits.MaxSupportAttachments im Backend passen. */
export const MAX_SUPPORT_ATTACHMENTS = 5;

export interface SupportTicketListItem {
  id: number;
  category: SupportCategory;
  subject: string;
  status: SupportStatus;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  createdByName: string | null;
  /** Nur fuer Support-Nutzer gesetzt. */
  createdByEmail: string | null;
  isOwn: boolean;
  messageCount: number;
  lastMessageFromSupport: boolean;
}

export interface SupportAttachment {
  id: number;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface SupportMessage {
  id: number;
  authorName: string | null;
  isFromSupport: boolean;
  isOwn: boolean;
  content: string;
  createdAt: string;
  attachments: SupportAttachment[];
}

export interface SupportTicket extends SupportTicketListItem {
  closedByName: string | null;
  canManage: boolean;
  canClose: boolean;
  messages: SupportMessage[];
}

export const unwrap = (res: any) => res?.data ?? res;

export const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Prueft neu gewaehlte Dateien gegen Typ, Groesse und Anzahl und wandelt HEIC in JPEG um. Gibt entweder die
 * vollstaendige neue Auswahl oder eine Fehlermeldung zurueck.
 */
export async function addAttachments(current: File[], added: File[]): Promise<{ files: File[] } | { error: string }> {
  const invalid = added.find((f) => !isAllowedSupportAttachment(f));
  if (invalid) return { error: i18n.t('support:fileTypeError', { name: invalid.name }) };
  const tooLarge = added.find(exceedsUploadLimit);
  if (tooLarge) return { error: fileTooLargeMessage(tooLarge) };
  if (current.length + added.length > MAX_SUPPORT_ATTACHMENTS) {
    return { error: i18n.t('support:tooManyFiles', { max: MAX_SUPPORT_ATTACHMENTS }) };
  }
  const normalized = await Promise.all(added.map(normalizeDocumentFile));
  return { files: [...current, ...normalized] };
}
